import * as React from 'react';
import styled from 'styled-components';
import { memoizeFunction } from '@agilepoint/utils-common';
import { Textbox } from '../textbox';
import { Picker } from './picker';
import { ITimePickerProps, Meridiem } from './time-picker-props';
import { timeValidator } from './time-validator';
import { HostableSurfaceContext, IHostableSurfaceContextProps } from '../hostable-surface';
import { getTimeAsString, parseTime, ParsedTime, convertToValidHour } from './utils';
import { KeyStrokeListener } from '../../utils';

export interface ITimePickerState {
    time: string;
    isCallOutVisible: boolean;
    hour: string;
    minute: string;
    second: string;
    meridiem: Meridiem;
}

const KEYS_TO_LISTEN: { [key: string]: number } = {
    arrowDown: 40,
    arrowUp: 38
};

const getValidCursorPosition = memoizeFunction((position = 0): number => {
    if (position === 2 || position === 5 || position === 8) {
        return ++position;
    } else if (position === 3 || position === 6 || position === 10) {
        return position - 1;
    }
    return position;
});

function formatValue(value = '00', cursorPosition?: number): string {
    cursorPosition = cursorPosition || 0;
    if (value.length === 2) {
        return value;
    }
    if (cursorPosition === 1 || cursorPosition === 4 || cursorPosition === 7 || cursorPosition === 9) {
        return `${value.substr(0, 1)}${value.substr(2, 1) || '0'}`;
    }
    return `${value.substr(0, 1)}${value.substr(1, 1) || '0'}`;


}

function formatHour(hour: string, is12Hour = false) {
    if (is12Hour) {
        if (Number(hour) > 12) {
            return '10';
        }
        if (hour === '00') {
            return '01';
        }
    }
    return hour;
}

const Wrapper = styled.div.attrs(() => ({
    className: String('ap-timePicker-wrapper')
}))`
    display: inline-block;
    position: relative;
    width: 240px;
    height: 40px;
    
    ${Textbox} {
        width: 100%;
        height: 100%;
    }
`;

function getInitialState(props: ITimePickerProps): ITimePickerState {
    const { defaultValue, value = defaultValue, is12Hour, showSecond, timeSeparator } = props;
    if (value) {
        const [hour, minute, second, meridiem] = parseTime(value, timeSeparator!);
        const time = getTimeAsString({ hour, minute, second, meridiem }, { showSecond, is12Hour, timeSeparator: timeSeparator! });
        const isValid = timeValidator.isValidTime(time, timeSeparator!);
        if (isValid) {
            return {
                meridiem,
                hour,
                isCallOutVisible: false,
                minute,
                second,
                time
            };
        }
    }
    return {
        meridiem: Meridiem.AM,
        hour: '',
        isCallOutVisible: false,
        minute: '',
        second: '',
        time: ''
    };
}

export class TimePicker extends React.Component<ITimePickerProps, ITimePickerState> {
    static contextType = HostableSurfaceContext;
    static defaultProps = {
        is12Hour: false,
        showSecond: false,
        timeSeparator: ':'
    };

    declare context: IHostableSurfaceContextProps;

    private rootElementRef: React.RefObject<HTMLDivElement>;
    private timeFieldRef: React.RefObject<HTMLInputElement>;
    private keyStroke: KeyStrokeListener;

    private changeHourUsingArrowKeys = memoizeFunction((value: string | number, is12Hour: boolean, isIncrement: boolean): string => {
        const { hour } = this.state;
        value = isIncrement ? Number(value) + 1 : Number(value) - 1;
        if (is12Hour) {
            value = value === 0 ? 12 : value === 13 ? 1 : value;
        } else {
            value = value === 24 ? 0 : value === -1 ? 23 : value;
        }
        const newHour = `${value < 10 ? '0' : ''}${value}`;
        const validHour = timeValidator.isValidHour(newHour, is12Hour);
        if (!validHour) {
            return hour;
        }
        return newHour;
    });

    private changeMinuteUsingArrowKeys = memoizeFunction((value: string | number, isIncrement: boolean): string => {
        const { minute } = this.state;
        value = isIncrement ? Number(value) + 1 : Number(value) - 1;
        if (value === -1) {
            value = 59;
        } else if (value === 60) {
            value = 0;
        }
        const newMinute = `${value < 10 ? '0' : ''}${value}`;
        const validMinute = timeValidator.isValidMinute(newMinute) || '00';
        if (!validMinute) {
            return minute;
        }
        return newMinute;
    });

    private changeSecondUsingArrowKeys = memoizeFunction((value: string | number, isIncrement: boolean): string => this.changeMinuteUsingArrowKeys(value, isIncrement));

    private onCharacterRemoved = memoizeFunction((removedCharacter: string, time: string, position: number): [string, number] => {
        if (removedCharacter === this.props.timeSeparator) {
            time = `${time.substr(0, position - 1)}: ${time.substr(position)} `;
            position -= 1;
        } else if (removedCharacter === ' ') {
            time = `${time.substr(0, position - 1)} ${time.substr(position)} `;
            position -= 1;
        } else if (removedCharacter) {
            time = `${time.substr(0, position)}0${time.substr(position)} `;
        }
        return [time, position];
    });

    static getDerivedStateFromProps(props: ITimePickerProps, state: ITimePickerState) {
        const { time } = state;
        const { value = time, is12Hour = false, showSecond = false, timeSeparator } = props;
        if (value !== undefined) {
            const isValidTime = timeValidator.isValidTime(value, timeSeparator!);
            if (isValidTime) {
                const [hour, minute, second, meridiem] = parseTime(value, timeSeparator!);
                return {
                    meridiem,
                    hour,
                    minute,
                    second,
                    time: getTimeAsString({ hour, minute, second, meridiem }, { showSecond, is12Hour, timeSeparator: timeSeparator! })
                };
            }
            return {
                meridiem: '',
                hour: '',
                minute: '',
                second: '',
                time: ''
            };
        }
        return null;
    }

    constructor(props: ITimePickerProps) {
        super(props);
        this.rootElementRef = props.elementRef !== undefined ? props.elementRef : React.createRef<HTMLDivElement>();
        this.timeFieldRef = React.createRef<HTMLInputElement>();
        this.state = getInitialState(props);
        this.keyStroke = new KeyStrokeListener(this.timeFieldRef);
    }

    componentDidMount() {
        this.context.addSurfaceEventListener('surfaceClick', this._onSurfaceClick);
        this.keyStroke.subscribe(this.openPickerCallout, 'Enter');
        this.keyStroke.subscribe(this.closePickerCallout, 'Escape');
    }

    render() {
        const { className, hidden } = this.props;
        return hidden ? null : (
            <Wrapper ref={this.rootElementRef} className={className} onClick={this.openPickerCallout}>
                {this.renderTextField()}
                {this.renderCallout()}
            </Wrapper>
        );
    }

    componentWillUnmount() {
        this.context.removeSurfaceEventListener('surfaceClick', this._onSurfaceClick);
        this.keyStroke.unsubscribe();
    }

    /*Component Initialzation */
    private renderTextField(): React.ReactElement {
        const { disabled, placeholder, id, hideClearIcon, clearIconTitle = 'Clear Time', error, title } = this.props;
        const { time } = this.state;
        return (
            <Textbox id={id}
                title={title}
                placeholder={placeholder}
                disabled={disabled}
                elementRef={this.timeFieldRef}
                onChange={this.onTimeChange}
                onKeyDown={this.onKeyDown}
                value={time}
                iconName="ap-clock.outline"
                iconPosition="right"
                allowToClear={!hideClearIcon}
                error={error}
                clearIconTitle={clearIconTitle}
                autoComplete="off"
                onClear={this.resetTimePicker} />
        );
    }

    private renderCallout(): React.ReactElement | null {
        const { isCallOutVisible, meridiem, hour, minute, second } = this.state;
        const { hourStep, is12Hour, minuteStep, secondStep, showSecond, disabled } = this.props;
        return (
            <Picker meridiem={meridiem} hour={hour ? convertToValidHour(hour, is12Hour, meridiem) : ''}
                minute={minute}
                second={second}
                hourStep={hourStep}
                is12Hour={is12Hour}
                itemHeight={35}
                minuteStep={minuteStep}
                onChange={this.changeTimeAndCursorPosition}
                secondStep={secondStep}
                showSecond={showSecond}
                hidden={!(isCallOutVisible && !disabled)}
                onDismiss={this.closePickerCallout}
                targetRef={this.rootElementRef} />
        );
    }

    private _onSurfaceClick = (ev: React.MouseEvent) => {
        const { current } = this.rootElementRef;
        if (current === null || current.contains(ev.target as Node)) {
            return;
        }
        this.setState({
            isCallOutVisible: false
        });
    }

    private closePickerCallout = () => {
        this.setState({
            isCallOutVisible: false
        });
    }

    private onTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { time } = this.state;
        const { is12Hour, showSecond, timeSeparator } = this.props;
        const timeField = e.target as HTMLInputElement;
        let newTime = timeField.value;
        let cursorPosition = timeField.selectionEnd || 0;
        const isAdded = newTime.length > time.length;
        const removedCharacter = isAdded ? null : time[cursorPosition];

        if (removedCharacter) {
            [newTime, cursorPosition] = this.onCharacterRemoved(removedCharacter, newTime, cursorPosition);
        }

        const [hour, minute, second, meridiem = Meridiem.AM] = parseTime(newTime, timeSeparator!);
        const newHour = formatHour(formatValue(hour, cursorPosition), is12Hour);
        const newMinute = formatValue(minute, cursorPosition);
        const newSecond = formatValue(second, cursorPosition);
        const newMeridiem = meridiem[0].toUpperCase() === 'P' ? Meridiem.PM : Meridiem.AM;

        newTime = getTimeAsString({ hour: newHour, minute: newMinute, second: newSecond, meridiem: newMeridiem }, { showSecond, is12Hour, timeSeparator: timeSeparator! });

        const isValidTime = timeValidator.isValidTime(newTime, timeSeparator!);
        const validCursorPosition = getValidCursorPosition(cursorPosition);

        if (isValidTime) {
            this.changeTimeAndCursorPosition(newHour, newMinute, newSecond, newMeridiem, validCursorPosition);
        }
    }

    private onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const timeField = e.target as HTMLInputElement;
        const incrementTime = true;
        const decrementTime = false;
        const cursorPosition = timeField.selectionEnd || 0;
        switch (e.keyCode) {
            case KEYS_TO_LISTEN['arrowUp']:
                e.preventDefault();
                this.changeTimeUsingArrowKeys(incrementTime, cursorPosition);
                break;
            case KEYS_TO_LISTEN['arrowDown']:
                e.preventDefault();
                this.changeTimeUsingArrowKeys(decrementTime, cursorPosition);
                break;
            default:
                break;
        }
    }

    private getFallBackValue(): ParsedTime {
        const { is12Hour } = this.props;
        return [(is12Hour ? '01' : '00'), '00', '00', Meridiem.AM];
    }

    private openPickerCallout = () => {
        const { disabled } = this.props;
        if (!disabled) {
            this.setState({
                isCallOutVisible: true
            });
        }
    }

    private toggleMeridiemUsingArrowKeys = (): Meridiem => {
        const { meridiem } = this.state;
        return meridiem.toUpperCase().startsWith('P') ? Meridiem.AM : Meridiem.PM;
    }

    /*Method responsible to trigger re-render of TimeField  and Picker*/
    private changeTimeAndCursorPosition = (hour: string, minute: string, second: string, meridiem: Meridiem, cursorPosition?: number) => {
        const { showSecond, is12Hour, timeSeparator } = this.props;
        const [defaultHour, defaultMinute, defaultSecond, defaultMeridiem] = this.getFallBackValue();
        const validHour = hour || defaultHour;
        const validMinute = minute || defaultMinute;
        const validSecond = second || defaultSecond;
        const validMeridiem = meridiem || defaultMeridiem;

        const timeObject = { hour: validHour, minute: validMinute, second: validSecond, meridiem: validMeridiem };

        const validTime = getTimeAsString(timeObject, { showSecond, is12Hour, timeSeparator: timeSeparator! });
        this.setState({
            meridiem: validMeridiem,
            hour: validHour,
            minute: validMinute,
            second: validSecond,
            time: validTime
        }, () => {
            this.setCursorPosition(cursorPosition);
        });
        this._onChange(validTime);
    }

    private setCursorPosition(cursorPosition = 0) {
        const timeField = this.timeFieldRef.current;
        if (timeField === null) {
            return;
        }
        timeField.setSelectionRange(cursorPosition, cursorPosition);
    }

    private resetTimePicker = (ev: React.MouseEvent) => {
        const { value } = this.props;
        ev.stopPropagation();
        if (value === undefined) {
            this.setState({
                meridiem: Meridiem.AM,
                hour: '',
                minute: '',
                second: '',
                time: ''
            });
        }
        this._onChange('');
    }

    private _onChange(time: string) {
        const { onChange } = this.props;
        if (!onChange) {
            return;
        }
        onChange(time);
    }

    private changeTimeUsingArrowKeys(isIncrement: boolean, cursorPosition: number) {
        const { is12Hour = false } = this.props;
        let { hour, minute, second, meridiem } = this.state;
        if (cursorPosition >= 0 && cursorPosition < 3) {
            hour = this.changeHourUsingArrowKeys(hour, is12Hour, isIncrement);
        } else if (cursorPosition > 2 && cursorPosition < 6) {
            minute = this.changeMinuteUsingArrowKeys(minute, isIncrement);
        } else if (cursorPosition > 5 && cursorPosition < 9) {
            second = this.changeSecondUsingArrowKeys(second, isIncrement);
        } else if (cursorPosition > 8 && cursorPosition < 12) {
            meridiem = this.toggleMeridiemUsingArrowKeys();
        }
        this.changeTimeAndCursorPosition(hour, minute, second, meridiem, cursorPosition);
    }
}
